import classNames from "classnames";
import { FC, useContext, useEffect, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import styles from "./SubsidiariesSelector.module.css";

const SUBSIDIARIES_FIELDS = ["id", "name"] as const;

type SubsidiariesSelectorProps = {
  isDisabled?: boolean;
  selectedSubsidiaries?: string[];
  onSubsidiaryClick: (subsidiaryIds: string[], totalSubsidiaries: number) => void;
};

const SubsidiariesSelector: FC<SubsidiariesSelectorProps> = ({
  isDisabled,
  selectedSubsidiaries,
  onSubsidiaryClick,
}) => {
  const aborter = useRef(new AbortController());
  const subsidiaryApi = useSubsidiariesApi();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const hasSyncedInitial = useRef(false);

  const subsidiariesSwr = subsidiaryApi.useUserSubsidiaries(
    { limit: 100, fields: SUBSIDIARIES_FIELDS },
    aborter.current.signal,
  );
  const [subs] = useDataInitializer(subsidiariesSwr.data, subsidiariesSwr.error);

  useEffect(() => {
    subsidiariesSwr.error && dispatch(showErrorNotification(i18n, subsidiariesSwr.error));
  }, [subsidiariesSwr.error]);

  const items = subs?.items || [];

  const isSelectAllChecked = items.length > 0 && items.length === selectedSubsidiaries?.length;

  const toggleById = (id: string) => {
    const next = selectedSubsidiaries?.includes(id)
      ? selectedSubsidiaries.filter((x) => x !== id)
      : [...(selectedSubsidiaries ?? []), id];
    onSubsidiaryClick(next, items.length);
  };

  const selectAll = (checked: boolean) => {
    if (checked) {
      const allIds = items.map((x) => x.id);
      onSubsidiaryClick(allIds, items.length);
    } else {
      onSubsidiaryClick([], items.length);
    }
  };

  useEffect(() => {
    if (!hasSyncedInitial.current && items?.length && selectedSubsidiaries) {
      onSubsidiaryClick(selectedSubsidiaries, items.length);
      hasSyncedInitial.current = true; // mark as synced
    }
  }, [items, selectedSubsidiaries, onSubsidiaryClick]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.selector}>
          <div
            className={classNames(styles["item-list"], { [styles.enabled]: !isDisabled }, styles.selectAll)}
            onClick={!isDisabled ? () => selectAll(!isSelectAllChecked) : undefined}
          >
            <div>{i18n.t.common.selectAll}</div>
            <input
              type="checkbox"
              className={classNames(styles.checkbox, { [styles.enable]: !isDisabled })}
              checked={isSelectAllChecked}
              onChange={!isDisabled ? () => selectAll(!isSelectAllChecked) : undefined}
              disabled={isDisabled || items.length === 0}
            />
          </div>
          {items.map((s) => {
            return (
              <div
                key={s.id}
                className={classNames(styles["item-list"], { [styles.enabled]: !isDisabled })}
                onClick={!isDisabled ? () => toggleById(s.id) : undefined}
              >
                <div className={classNames(styles.title, { [styles.enable]: !isDisabled })}>{s.name}</div>

                <input
                  id={s.id}
                  type="checkbox"
                  className={classNames(styles.checkbox, { [styles.enable]: !isDisabled })}
                  disabled={isDisabled}
                  checked={!!selectedSubsidiaries?.includes(s.id)}
                  onChange={!isDisabled ? () => toggleById(s.id) : undefined}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default SubsidiariesSelector;
